import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, ArrowLeft, Clock, User, FileText, Facebook, Linkedin, Mail, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const BlogueArticle = () => {
  const { slug } = useParams();

  const { data: post, isLoading } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground">Chargement de l'article...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-4xl font-bold mb-4">Article non trouvé</h1>
        <p className="text-muted-foreground mb-8">Cet article n'existe pas ou n'est plus disponible.</p>
        <Link to="/blogue">
          <Button>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour au blogue
          </Button>
        </Link>
      </div>
    );
  }

  const typedPost = post as any;
  const currentUrl = window.location.href;
  const encodedUrl = encodeURIComponent(currentUrl);
  const encodedTitle = encodeURIComponent(post.title);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    x: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodeURIComponent(`Découvrez cet article : ${currentUrl}`)}`,
  };

  return (
    <>
      <Helmet>
        <title>{post.title} - Agroref</title>
        <meta name="description" content={post.excerpt || post.title} />
      </Helmet>

      <article className="max-w-4xl mx-auto">
        <Link to="/blogue">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour au blogue
          </Button>
        </Link>

        {post.image_url && (
          <div className="aspect-video overflow-hidden rounded-lg mb-8">
            <img
              src={post.image_url}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <header className="mb-8">
          <div className="mb-3">
            <span className="inline-block bg-destructive text-white text-sm font-bold px-4 py-1.5 rounded">
              AGRO
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl font-extrabold mb-4 leading-tight">{post.title}</h1>

          <div className="flex flex-wrap items-center gap-4 text-muted-foreground text-sm">
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              <span>Temps de lecture: {typedPost.reading_time || 5} minutes</span>
            </div>
            <div className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              <span>{typedPost.author_name || "Agroref"}</span>
            </div>
          </div>
        </header>

        <div
          className="prose prose-lg max-w-none [&_img]:rounded-lg [&_img]:my-6 [&_img]:max-w-full [&_p]:mb-6 [&_p+p]:mt-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {typedPost.pdf_url && (
          <div className="mt-10">
            <a
              href={typedPost.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="gap-2">
                <FileText className="h-5 w-5" />
                {typedPost.pdf_label || "En savoir plus"}
              </Button>
            </a>
          </div>
        )}

        {/* Share section */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex items-center gap-3 flex-wrap">
            <Share2 className="h-5 w-5 text-muted-foreground" />
            <span className="font-semibold text-sm text-muted-foreground">Partager cet article :</span>
            <div className="flex gap-2">
              <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="icon" className="rounded-full hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-colors">
                  <Facebook className="h-4 w-4" />
                </Button>
              </a>
              <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="icon" className="rounded-full hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-colors">
                  <Linkedin className="h-4 w-4" />
                </Button>
              </a>
              <a href={shareLinks.x} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="icon" className="rounded-full hover:bg-black hover:text-white hover:border-black transition-colors">
                  <XIcon className="h-4 w-4" />
                </Button>
              </a>
              <a href={shareLinks.email}>
                <Button variant="outline" size="icon" className="rounded-full hover:bg-muted transition-colors">
                  <Mail className="h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogueArticle;
